import Phaser from 'phaser';
import type { CharacterRole, CourtIncidentType } from '../types/game';
import { DataRegistry } from '../systems/DataRegistry';
import { VisualTheme } from './VisualTheme';

const ROLE_SHAPES: Record<string, { crown: number; robe: number; prop: 'scepter' | 'scroll' | 'blade' | 'book' | 'none' }> = {
  ruler: { crown: 3, robe: 1, prop: 'scepter' },
  heir: { crown: 2, robe: 2, prop: 'none' },
  consort: { crown: 2, robe: 3, prop: 'none' },
  chancellor: { crown: 1, robe: 2, prop: 'scroll' },
  treasurer: { crown: 1, robe: 4, prop: 'book' },
  spymaster: { crown: 0, robe: 5, prop: 'blade' },
  high_priest: { crown: 1, robe: 6, prop: 'book' },
  governor: { crown: 1, robe: 2, prop: 'scroll' },
  general: { crown: 1, robe: 7, prop: 'blade' },
  admiral: { crown: 1, robe: 8, prop: 'blade' },
};

export function ensureCourtPortraitTexture(scene: Phaser.Scene, factionId: string, role: CharacterRole | string) {
  const normalizedRole = normalizeRole(role);
  const key = `court.production.${factionId}.${normalizedRole}`;
  if (scene.textures.exists(key)) return key;
  const faction = DataRegistry.instance.factions[factionId];
  const primary = color(faction?.colors.primary, 0x7a2527);
  const secondary = color(faction?.colors.secondary, 0x1b1b1b);
  const accent = color(faction?.colors.accent, VisualTheme.colors.goldBright);
  const graphics = scene.make.graphics({ x: 0, y: 0 });
  const width = 512;
  const height = 512;
  const shape = ROLE_SHAPES[normalizedRole] ?? ROLE_SHAPES.heir;

  graphics.fillStyle(0x070b10, 1).fillRect(0, 0, width, height);
  for (let band = 0; band < 9; band++) {
    graphics.fillStyle(Phaser.Display.Color.Interpolate.ColorWithColor(
      Phaser.Display.Color.ValueToColor(secondary),
      Phaser.Display.Color.ValueToColor(0x09121a),
      9,
      band,
    ).color, 1).fillRect(0, band * 58, width, 60);
  }
  graphics.fillStyle(primary, 0.16).fillCircle(256, 225, 218);
  graphics.lineStyle(8, accent, 0.76).strokeCircle(256, 225, 202);
  graphics.lineStyle(2, 0xf4e0af, 0.35).strokeCircle(256, 225, 183);

  for (let corner = 0; corner < 4; corner++) {
    const x = corner % 2 ? 482 : 30;
    const y = corner > 1 ? 482 : 30;
    graphics.lineStyle(5, accent, 0.7);
    graphics.beginPath().moveTo(x - (corner % 2 ? 38 : -38), y).lineTo(x, y).lineTo(x, y - (corner > 1 ? 38 : -38)).strokePath();
    graphics.fillStyle(accent, 0.65).fillCircle(x, y, 7);
  }

  const skin = normalizedRole === 'consort' ? 0xc99065 : 0xb87a50;
  const skinLight = normalizedRole === 'consort' ? 0xe0b083 : 0xd39a6c;
  const hair = ['portuguese', 'dutch_voc', 'british_eic'].includes(factionId) ? 0x5b3d2a : factionId === 'ming' ? 0x111111 : 0x241810;
  const shoulderY = 420;
  graphics.fillStyle(0x000000, 0.35).fillEllipse(256, 454, 350, 58);
  graphics.fillStyle(secondary, 1).fillEllipse(256, shoulderY, 330, 210);
  graphics.fillStyle(primary, 1).fillRoundedRect(118, 337, 276, 160, 54);
  graphics.lineStyle(8, accent, 0.84).strokeRoundedRect(118, 337, 276, 160, 54);
  graphics.fillStyle(accent, 0.34).fillRect(238, 340, 36, 157);
  for (let stripe = 0; stripe < 5; stripe++) {
    graphics.fillStyle(stripe % 2 ? secondary : accent, stripe % 2 ? 0.55 : 0.28).fillRect(145 + stripe * 52, 360, 24, 123);
  }

  graphics.fillStyle(skin, 1).fillRoundedRect(203, 214, 106, 142, 38);
  graphics.fillStyle(skinLight, 1).fillEllipse(256, 202, 138, 166);
  graphics.fillStyle(hair, 1).fillEllipse(256, 153, 145, 105);
  graphics.fillStyle(hair, 1).fillRoundedRect(188, 142, 29, 120, 12);
  graphics.fillStyle(hair, 1).fillRoundedRect(295, 142, 29, 120, 12);

  graphics.fillStyle(0x241710, 1).fillEllipse(225, 205, 12, 8);
  graphics.fillStyle(0x241710, 1).fillEllipse(287, 205, 12, 8);
  graphics.lineStyle(4, 0x6e3e2c, 0.8).lineBetween(241, 253, 271, 253);
  graphics.lineStyle(3, 0x6e3e2c, 0.5).lineBetween(253, 216, 248, 235).lineBetween(248, 235, 261, 236);

  if (normalizedRole !== 'consort' && normalizedRole !== 'heir') {
    graphics.fillStyle(hair, 0.92).fillRoundedRect(221, 261, 70, 30, 12);
    graphics.fillStyle(hair, 0.82).fillTriangle(226, 280, 256, 322, 286, 280);
  }

  drawHeadwear(graphics, factionId, normalizedRole, shape.crown, primary, secondary, accent);
  drawNecklace(graphics, accent, shape.robe);
  drawProp(graphics, shape.prop, accent, secondary);

  graphics.lineStyle(12, 0x050607, 0.92).strokeRect(6, 6, width - 12, height - 12);
  graphics.lineStyle(5, accent, 0.92).strokeRect(14, 14, width - 28, height - 28);
  graphics.lineStyle(2, 0xf8e8bd, 0.4).strokeRect(23, 23, width - 46, height - 46);
  graphics.generateTexture(key, width, height);
  graphics.destroy();
  return key;
}

export function ensureCourtEventTexture(scene: Phaser.Scene, type: CourtIncidentType | string) {
  const key = `court.production.event.${type}`;
  if (scene.textures.exists(key)) return key;
  const graphics = scene.make.graphics({ x: 0, y: 0 });
  const width = 1200;
  const height = 675;
  const danger = ['coup', 'rebellion', 'succession_crisis', 'funeral'].includes(type);
  const sky = danger ? 0x2b1820 : type === 'marriage' || type === 'birth' || type === 'coronation' ? 0x5f3825 : 0x1b3540;
  const glow = danger ? 0xb24a32 : 0xe0aa55;

  graphics.fillStyle(0x070a0e, 1).fillRect(0, 0, width, height);
  for (let y = 0; y < height; y += 18) {
    const t = y / height;
    const band = Phaser.Display.Color.Interpolate.ColorWithColor(Phaser.Display.Color.ValueToColor(sky), Phaser.Display.Color.ValueToColor(0x080b10), height, Math.round(t * height)).color;
    graphics.fillStyle(band, 1).fillRect(0, y, width, 20);
  }
  graphics.fillStyle(glow, 0.18).fillCircle(600, 250, 310);
  graphics.fillStyle(0x10141a, 0.82).fillRect(0, 470, width, 205);

  for (let column = 0; column < 8; column++) {
    const x = 72 + column * 151;
    graphics.fillStyle(0x201710, 1).fillRect(x, 100, 44, 410);
    graphics.fillStyle(0x5f4328, 1).fillRect(x + 8, 100, 28, 410);
    graphics.fillStyle(0xb98942, 0.9).fillTriangle(x - 24, 112, x + 22, 55, x + 68, 112);
    graphics.lineStyle(4, 0xd5a85b, 0.66).strokeTriangle(x - 24, 112, x + 22, 55, x + 68, 112);
  }
  graphics.fillStyle(0x302218, 1).fillRect(155, 474, 890, 55);
  graphics.lineStyle(7, 0xc89b4d, 0.75).strokeRect(155, 474, 890, 55);

  if (type === 'funeral') drawFuneral(graphics, glow);
  else if (type === 'coup' || type === 'rebellion') drawCrisis(graphics, type === 'coup', glow);
  else if (type === 'marriage') drawMarriage(graphics, glow);
  else if (type === 'birth') drawBirth(graphics, glow);
  else if (type === 'regency' || type === 'succession_crisis') drawRegency(graphics, glow);
  else drawCoronation(graphics, glow);

  graphics.fillStyle(0x030406, 0.42).fillRect(0, 0, width, 82);
  graphics.fillStyle(0x030406, 0.52).fillRect(0, height - 70, width, 70);
  graphics.lineStyle(8, 0x050607, 1).strokeRect(4, 4, width - 8, height - 8);
  graphics.lineStyle(3, VisualTheme.colors.goldBright, 0.8).strokeRect(15, 15, width - 30, height - 30);
  graphics.generateTexture(key, width, height);
  graphics.destroy();
  return key;
}

function drawHeadwear(graphics: Phaser.GameObjects.Graphics, factionId: string, role: string, crown: number, primary: number, secondary: number, accent: number) {
  if (['portuguese', 'dutch_voc', 'british_eic'].includes(factionId)) {
    graphics.fillStyle(secondary, 1).fillEllipse(256, 128, 178, 42);
    graphics.fillStyle(primary, 1).fillRoundedRect(190, 91, 132, 48, 12);
    graphics.lineStyle(5, accent, 1).strokeRoundedRect(190, 91, 132, 48, 12);
    return;
  }
  if (factionId === 'ming') {
    graphics.fillStyle(secondary, 1).fillRoundedRect(194, 75, 124, 74, 16);
    graphics.fillStyle(accent, 1).fillRect(181, 99, 150, 15);
    graphics.fillStyle(primary, 1).fillCircle(256, 91, 17);
    return;
  }
  if (['malacca', 'aceh', 'demak', 'cirebon', 'banten', 'arab_confederation'].includes(factionId)) {
    graphics.fillStyle(0xf0e5c9, 1).fillEllipse(256, 120, 174, 68);
    graphics.fillStyle(primary, 1).fillRoundedRect(205, 90, 102, 47, 18);
    graphics.lineStyle(4, accent, 1).strokeEllipse(256, 120, 174, 68);
    return;
  }
  graphics.fillStyle(primary, 1).fillTriangle(184, 139, 256, 45 - crown * 5, 328, 139);
  graphics.lineStyle(6, accent, 1).strokeTriangle(184, 139, 256, 45 - crown * 5, 328, 139);
  graphics.fillStyle(accent, 0.95).fillCircle(256, 75, 13 + crown * 2);
  graphics.fillStyle(secondary, 1).fillTriangle(204, 135, 225, 85, 242, 135);
  graphics.fillStyle(secondary, 1).fillTriangle(270, 135, 287, 85, 307, 135);
  if (role === 'consort') graphics.fillStyle(accent, 0.75).fillEllipse(256, 142, 198, 18);
}

function drawNecklace(graphics: Phaser.GameObjects.Graphics, accent: number, style: number) {
  graphics.lineStyle(7, accent, 0.9).beginPath().arc(256, 325, 70 + style * 2, Phaser.Math.DegToRad(22), Phaser.Math.DegToRad(158), false).strokePath();
  for (let bead = 0; bead < 7; bead++) {
    const angle = Phaser.Math.DegToRad(28 + bead * 20);
    graphics.fillStyle(accent, 0.95).fillCircle(256 + Math.cos(angle) * 70, 325 + Math.sin(angle) * 46, 6);
  }
}

function drawProp(graphics: Phaser.GameObjects.Graphics, prop: 'scepter' | 'scroll' | 'blade' | 'book' | 'none', accent: number, secondary: number) {
  if (prop === 'none') return;
  if (prop === 'scepter') {
    graphics.lineStyle(10, accent, 1).lineBetween(365, 306, 410, 478);
    graphics.fillStyle(accent, 1).fillCircle(362, 296, 18);
    graphics.fillStyle(secondary, 1).fillCircle(362, 296, 8);
  } else if (prop === 'scroll') {
    graphics.fillStyle(0xe7d4aa, 1).fillRoundedRect(336, 359, 104, 72, 12);
    graphics.lineStyle(4, accent, 1).strokeRoundedRect(336, 359, 104, 72, 12);
    for (let line = 0; line < 4; line++) graphics.lineStyle(2, secondary, 0.7).lineBetween(353, 375 + line * 12, 421, 375 + line * 12);
  } else if (prop === 'book') {
    graphics.fillStyle(secondary, 1).fillRoundedRect(332, 365, 112, 84, 8);
    graphics.lineStyle(5, accent, 1).strokeRoundedRect(332, 365, 112, 84, 8);
    graphics.lineStyle(3, accent, 0.65).lineBetween(388, 370, 388, 444);
  } else {
    graphics.lineStyle(12, 0x9f7950, 1).lineBetween(352, 338, 425, 470);
    graphics.lineStyle(8, 0xe7edf0, 1).lineBetween(353, 338, 320, 275);
    graphics.fillStyle(accent, 1).fillTriangle(312, 258, 325, 289, 343, 273);
  }
}

function drawFigure(graphics: Phaser.GameObjects.Graphics, x: number, y: number, primary: number, accent: number, scale = 1) {
  graphics.fillStyle(0x000000, 0.25).fillEllipse(x, y + 158 * scale, 105 * scale, 24 * scale);
  graphics.fillStyle(primary, 1).fillRoundedRect(x - 45 * scale, y + 48 * scale, 90 * scale, 124 * scale, 24 * scale);
  graphics.lineStyle(5 * scale, accent, 0.9).strokeRoundedRect(x - 45 * scale, y + 48 * scale, 90 * scale, 124 * scale, 24 * scale);
  graphics.fillStyle(0xc79067, 1).fillCircle(x, y + 16 * scale, 37 * scale);
  graphics.fillStyle(0x211710, 1).fillEllipse(x, y - 8 * scale, 78 * scale, 42 * scale);
  graphics.fillStyle(accent, 1).fillTriangle(x - 42 * scale, y - 6 * scale, x, y - 70 * scale, x + 42 * scale, y - 6 * scale);
}

function drawCoronation(graphics: Phaser.GameObjects.Graphics, glow: number) {
  graphics.fillStyle(0x6d2b27, 1).fillRoundedRect(474, 280, 252, 245, 28);
  graphics.lineStyle(8, glow, 0.9).strokeRoundedRect(474, 280, 252, 245, 28);
  drawFigure(graphics, 600, 260, 0x8c2528, glow, 1.35);
  for (const x of [285, 390, 810, 915]) drawFigure(graphics, x, 370, x < 600 ? 0x2b4f45 : 0x3d335e, glow, 0.68);
  graphics.fillStyle(glow, 0.45).fillCircle(600, 160, 88);
}

function drawMarriage(graphics: Phaser.GameObjects.Graphics, glow: number) {
  drawFigure(graphics, 520, 300, 0x8c2528, glow, 1.1);
  drawFigure(graphics, 680, 300, 0x385a65, glow, 1.1);
  graphics.lineStyle(9, glow, 0.95).beginPath().arc(600, 305, 165, Phaser.Math.DegToRad(205), Phaser.Math.DegToRad(335), false).strokePath();
  for (const x of [270, 370, 830, 930]) drawFigure(graphics, x, 390, 0x453c52, glow, 0.6);
}

function drawBirth(graphics: Phaser.GameObjects.Graphics, glow: number) {
  drawFigure(graphics, 460, 320, 0x8c2528, glow, 1.05);
  drawFigure(graphics, 745, 330, 0x385a65, glow, 0.95);
  graphics.fillStyle(0xf1d7ab, 1).fillRoundedRect(547, 382, 116, 70, 22);
  graphics.fillStyle(0xc79067, 1).fillCircle(605, 384, 25);
  graphics.fillStyle(glow, 0.36).fillCircle(605, 380, 132);
}

function drawFuneral(graphics: Phaser.GameObjects.Graphics, glow: number) {
  graphics.fillStyle(0x16191d, 1).fillRoundedRect(408, 365, 384, 105, 22);
  graphics.lineStyle(8, glow, 0.75).strokeRoundedRect(408, 365, 384, 105, 22);
  graphics.fillStyle(0x73614b, 1).fillTriangle(455, 365, 600, 230, 745, 365);
  for (const x of [260, 350, 850, 940]) drawFigure(graphics, x, 375, 0x23262c, glow, 0.65);
  for (let flame = 0; flame < 7; flame++) {
    const x = 490 + flame * 36;
    graphics.fillStyle(0xdf6c35, 0.9).fillTriangle(x - 12, 360, x, 315 - (flame % 3) * 14, x + 12, 360);
  }
}

function drawCrisis(graphics: Phaser.GameObjects.Graphics, coup: boolean, glow: number) {
  drawFigure(graphics, 600, 260, 0x7f2428, glow, 1.2);
  for (const x of [220, 330, 440, 760, 870, 980]) {
    drawFigure(graphics, x, 390, x < 600 ? 0x263845 : 0x46322a, glow, 0.62);
    graphics.lineStyle(7, 0xd6dadd, 0.95).lineBetween(x + 26, 405, x - 12, 272);
  }
  if (coup) graphics.fillStyle(glow, 0.6).fillTriangle(500, 210, 600, 85, 700, 210);
  else for (let torch = 0; torch < 5; torch++) graphics.fillStyle(0xe66531, 0.85).fillCircle(300 + torch * 150, 236 + (torch % 2) * 24, 22);
}

function drawRegency(graphics: Phaser.GameObjects.Graphics, glow: number) {
  drawFigure(graphics, 545, 330, 0x8c2528, glow, 0.82);
  drawFigure(graphics, 690, 275, 0x303d55, glow, 1.15);
  graphics.fillStyle(0x6d2b27, 1).fillRoundedRect(475, 405, 250, 116, 22);
  graphics.lineStyle(7, glow, 0.85).strokeRoundedRect(475, 405, 250, 116, 22);
  for (const x of [285, 385, 815, 915]) drawFigure(graphics, x, 397, 0x3e3b47, glow, 0.58);
}

function normalizeRole(role: CharacterRole | string) {
  if (role === 'child' || role === 'claimant' || role === 'courtier') return 'heir';
  if (role === 'regent' || role === 'advisor') return 'chancellor';
  return role;
}

function color(value: string | undefined, fallback: number) {
  return value ? Phaser.Display.Color.HexStringToColor(value).color : fallback;
}
